import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Edit2, Check, X, Image, Type, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Editable Text Component
export const EditableText = ({ 
  pageId, 
  sectionId, 
  fieldId, 
  defaultValue, 
  className = '', 
  as = 'p',
  style = {}
}) => {
  const { isAdmin } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [savedValue, setSavedValue] = useState(defaultValue);
  const [saving, setSaving] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Load saved value from backend
    loadValue();
  }, [pageId, sectionId, fieldId]);

  const loadValue = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pages/${pageId}/${sectionId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.content?.[fieldId]) {
          setValue(data.content[fieldId]);
          setSavedValue(data.content[fieldId]);
        }
      }
    } catch (error) {
      // Use default value
    }
  };

  const saveValue = async () => {
    setSaving(true);
    try {
      // First get existing content
      let existingContent = {};
      try {
        const getRes = await fetch(`${API_URL}/api/pages/${pageId}/${sectionId}`);
        if (getRes.ok) {
          const data = await getRes.json();
          existingContent = data.content || {};
        }
      } catch (e) {}

      // Update with new value
      const response = await fetch(`${API_URL}/api/pages/${pageId}/${sectionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          content: { ...existingContent, [fieldId]: value } 
        })
      });
      
      if (response.ok) {
        setSavedValue(value);
        setIsEditing(false);
      } else {
        alert('Failed to save. Make sure you are logged in as admin.');
        setValue(savedValue);
      }
    } catch (error) {
      console.error('Error saving:', error);
      setValue(savedValue);
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setValue(savedValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && as !== 'textarea') {
      e.preventDefault();
      saveValue();
    }
    if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const Component = as;

  if (!isAdmin) {
    return <Component className={className} style={style}>{value}</Component>;
  }

  if (isEditing) {
    return (
      <div className="relative inline-block w-full">
        {as === 'textarea' || value.length > 100 ? (
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`${className} border-2 border-blue-500 rounded-lg p-2 bg-white/95 w-full min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-400`}
            style={style}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`${className} border-2 border-blue-500 rounded-lg px-2 py-1 bg-white/95 w-full focus:outline-none focus:ring-2 focus:ring-blue-400`}
            style={style}
          />
        )}
        <div className="absolute -top-10 right-0 flex gap-1 bg-white rounded-lg shadow-lg p-1 border border-gray-200">
          <button
            onClick={saveValue}
            disabled={saving}
            className="p-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          </button>
          <button
            onClick={cancelEdit}
            className="p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative inline-block group cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => setIsEditing(true)}
    >
      <Component className={`${className} ${isHovered ? 'outline outline-2 outline-dashed outline-purple-400 outline-offset-2 rounded' : ''}`} style={style}>
        {value}
      </Component>
      {isHovered && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-50">
          <Edit2 size={12} className="inline mr-1" /> Click to edit
        </div>
      )}
    </div>
  );
};

// Editable Image Component
export const EditableImage = ({ 
  pageId, 
  sectionId, 
  fieldId, 
  defaultSrc, 
  alt = '',
  className = '' 
}) => {
  const { isAdmin } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [src, setSrc] = useState(defaultSrc);
  const [savedSrc, setSavedSrc] = useState(defaultSrc);
  const [newUrl, setNewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    loadValue();
  }, [pageId, sectionId, fieldId]);

  const loadValue = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pages/${pageId}/${sectionId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.content?.[fieldId]) {
          setSrc(data.content[fieldId]);
          setSavedSrc(data.content[fieldId]);
        }
      }
    } catch (error) {
      // Use default
    }
  };

  const saveValue = async () => {
    if (!newUrl) return;
    setSaving(true);
    try {
      let existingContent = {};
      try {
        const getRes = await fetch(`${API_URL}/api/pages/${pageId}/${sectionId}`);
        if (getRes.ok) {
          const data = await getRes.json();
          existingContent = data.content || {};
        }
      } catch (e) {}

      const response = await fetch(`${API_URL}/api/pages/${pageId}/${sectionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          content: { ...existingContent, [fieldId]: newUrl } 
        })
      });
      
      if (response.ok) {
        setSrc(newUrl);
        setSavedSrc(newUrl);
        setIsEditing(false);
        setNewUrl('');
      } else {
        alert('Failed to save. Make sure you are logged in as admin.');
      }
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return <img src={src} alt={alt} className={className} />;
  }

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img 
        src={src} 
        alt={alt} 
        className={`${className} ${isHovered && !isEditing ? 'outline outline-2 outline-dashed outline-purple-400 outline-offset-2 rounded' : ''}`} 
      />
      
      {isHovered && !isEditing && (
        <button
          onClick={() => setIsEditing(true)}
          className="absolute top-2 right-2 p-2 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 transition-colors z-10"
        >
          <Image size={16} />
        </button>
      )}

      {isEditing && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-lg z-20">
          <div className="bg-white rounded-xl p-4 m-4 w-full max-w-sm shadow-2xl">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center">
              <Image size={18} className="mr-2 text-blue-600" />
              Update Image
            </h4>
            <input
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="Enter new image URL..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {newUrl && (
              <img src={newUrl} alt="Preview" className="w-full h-24 object-cover rounded-lg mb-3 border" />
            )}
            <div className="flex gap-2">
              <button
                onClick={saveValue}
                disabled={saving || !newUrl}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <><Check size={16} className="mr-1" /> Save</>}
              </button>
              <button
                onClick={() => { setIsEditing(false); setNewUrl(''); }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Edit Mode Toggle
export const AdminEditToggle = () => {
  const { isAdmin } = useAuth();
  const [editMode, setEditMode] = useState(true);

  if (!isAdmin) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div className="bg-white rounded-2xl shadow-2xl border border-blue-200 p-4">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
          <span className="text-sm font-semibold text-gray-700">Admin Mode</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">Click any text or image to edit</p>
      </div>
    </div>
  );
};

export default { EditableText, EditableImage, AdminEditToggle };
